import { useMemo } from "react";

const formatQty = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v || "");
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 }).format(n);
};
const formatMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  // Espace insecable milliers + virgule decimale (aligne sur le PDF)
  return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }).format(n);
};
const formatPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

/**
 * Apercu en direct du tableau des lignes tel qu'il sortira sur le PDF.
 * Nom en gras + description multi-lignes en dessous. Rendu local, aucune requete serveur.
 * Repliable, masque par defaut sous 1200 px (utiliser className parent pour la visibilite).
 */
export default function LiveDocumentLinesPreview({ items = [], products = [] }) {
  const rows = useMemo(() => {
    return items.map((it) => {
      const linked = products.find(p => p.id === it.product_id);
      const ref = linked?.reference || it.reference || "";
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      const tva = Number(it.tva_rate);
      const total = qty * price;
      return { name: it.product_name || "", description: it.description || "", ref, qty, price, tva: Number.isFinite(tva) ? tva : 20, total };
    });
  }, [items, products]);

  const totalHt = rows.reduce((s, r) => s + r.total, 0);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid="live-lines-preview">
      <div className="uppercase text-[10px] tracking-wider text-slate-500 mb-2 font-semibold">Aperçu du tableau (rendu final)</div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "46%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-100 text-slate-700 text-[10px] uppercase">
              <th className="text-left px-2 py-1 border border-slate-200">Libellé</th>
              <th className="text-center px-1 py-1 border border-slate-200">Réf.</th>
              <th className="text-center px-1 py-1 border border-slate-200">Qté</th>
              <th className="text-right px-1 py-1 border border-slate-200">% TVA</th>
              <th className="text-right px-1 py-1 border border-slate-200">Unit. HT (€)</th>
              <th className="text-right px-1 py-1 border border-slate-200">Net HT (€)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-3 text-slate-400 italic border border-slate-200">Aucune ligne</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="align-top">
                <td className="px-2 py-1 border border-slate-200 break-words">
                  <div className="font-semibold text-slate-900">{r.name || <span className="text-slate-400">(sans nom)</span>}</div>
                  {r.description && (
                    <div className="whitespace-pre-line text-slate-600 mt-0.5">{r.description}</div>
                  )}
                </td>
                <td className="text-center px-1 py-1 border border-slate-200 font-mono text-[10px] text-slate-600 break-all">{r.ref}</td>
                <td className="text-center px-1 py-1 border border-slate-200 font-mono">{formatQty(r.qty)}</td>
                <td className="text-right px-1 py-1 border border-slate-200 font-mono">{formatPct(r.tva)} %</td>
                <td className="text-right px-1 py-1 border border-slate-200 font-mono">{formatMoney(r.price)}</td>
                <td className="text-right px-1 py-1 border border-slate-200 font-mono">{formatMoney(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex justify-end text-slate-600 gap-2">
        <span className="text-[10px] uppercase tracking-wider">Total HT (aperçu) :</span>
        <span className="font-mono font-semibold text-slate-900" data-testid="live-preview-total-ht">{formatMoney(totalHt)} €</span>
      </div>
    </div>
  );
}
